// alternative-dashboard/alternative-components/CashPosition.tsx
import { memo, useEffect, useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { t } from "i18next";
import { useCountry } from "@/app/services/context/CountryContext";
import { gxDashboardAccountsCashPositionApi } from "@/app/apiConfig/services/customerAbstractApiLayer"; //  confirm import path
import { gxIsArray } from "@/app/utils/helpers";
import { mapCashPositionToChartPoints } from "../mappers/cashPositionMapper";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import "../../payments/paymentDashboard/PaymentDashboard.scss";

interface CashPositionProps {
  title?: string;
}

export interface DashboardAccountsCashPositionRawItem {
  accountid: string;
  transactiondatetime: string;
  balancecreditdebitindicator: string;
  balancecreditdebitamount: string;
  transactioncurrency: string;
  transactionPercentage: string;
}

type CashPositionRangeKey = "30D" | "90D" | "1Y";

const PERIOD_TABS: { key: CashPositionRangeKey; label: string }[] = [
  { key: "30D", label: "30 D" },
  { key: "90D", label: "90 D" },
  { key: "1Y", label: "1 Y" },
];

const RANGE_TO_DAYS: Record<CashPositionRangeKey, number> = {
  "30D": 30,
  "90D": 90,
  "1Y": 365,
};

const formatDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const buildDateRange = (range: CashPositionRangeKey) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - RANGE_TO_DAYS[range]);
  return { startDate: formatDate(start), endDate: formatDate(end) };
};

const formatAmount = (amount: number) => {
  if (amount >= 1_000_000_000) return Math.floor(amount / 1_000_000_000) + "B";
  if (amount >= 1_000_000) return Math.floor(amount / 1_000_000) + "m";
  if (amount >= 1_000) return Math.floor(amount / 1_000) + "k";
  return amount?.toString();
};

interface ChartTooltipPayloadEntry {
  value?: number | string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipPayloadEntry[];
  label?: string;
}

const CashPositionTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="gx-chat-tooltip">
      <p className="gx-chat-tooltip-label">{label}</p>
      <p className="gx-chat-tooltip-value">
        {t("value_")} {formatAmount(Number(payload[0].value))}
      </p>
    </div>
  );
};

const CashPosition = ({ title }: CashPositionProps) => {
  const { selectedCountry } = useCountry();
  const currencyCode = selectedCountry?.currency?.code ?? "";
  const [activePeriod, setActivePeriod] = useState<CashPositionRangeKey>("30D");

  const [rawData, setRawData] = useState<
    DashboardAccountsCashPositionRawItem[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!currencyCode) return;
    let isMounted = true;

    const loadCashPosition = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const { startDate, endDate } = buildDateRange(activePeriod);

        const payload = {
          pageNo: 0,
          pageSize: 0,
          filterBy2List: [
            { filterKey: "CURRENCY_CODE", filterStringValue: currencyCode },
            { filterKey: "START_DATE", filterStringValue: startDate },
            { filterKey: "END_DATE", filterStringValue: endDate },
          ],
        };

        const response = await gxDashboardAccountsCashPositionApi(payload);
        if (!isMounted) return;

        if (response?.status === 1 && gxIsArray(response?.data)) {
          setRawData(response.data);
        } else {
          setRawData([]);
        }
      } catch (err) {
        console.log("Error in loadCashPosition::", err);
        if (isMounted) {
          setHasError(true);
          setRawData([]);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadCashPosition();

    return () => {
      isMounted = false;
    };
  }, [currencyCode, activePeriod]);

  const data = useMemo(() => mapCashPositionToChartPoints(rawData), [rawData]);

  const periodLabel = useMemo(() => {
    if (activePeriod === "30D") return "last 30 days";
    if (activePeriod === "90D") return "last 90 days";
    return "last 1 year";
  }, [activePeriod]);

  return (
    <div className="gx-card-box-alter">
      <div className="gx-chattitle-child">
        <div className="gx-chat-title-alter">
          <h2 className="gx-h4-b">
            {title || t("cash_position")} {periodLabel}
          </h2>
          <span className="gx-p10-m">{t("closing_balance_accounts")}</span>
        </div>
        <div className="gx-chat-range-toggle">
          {PERIOD_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={activePeriod === tab.key ? "active" : ""}
              onClick={() => setActivePeriod(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="gx-chat relative">
        {isLoading && (
          <div className="gx-bene-form-container-loading-overlay">
            <SubmitLoading />
          </div>
        )}

        {!isLoading && hasError && (
          <p className="gx-p14-r gx-error-text">{t("cash_position_fetch_error")}</p>
        )}

        {!isLoading && !hasError && (
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart className="gx-font-medium" data={data}>
              <CartesianGrid vertical horizontal={false} stroke="#eef0f5" />
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1f3c88" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#1f3c88" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <XAxis
                tickLine={false}
                axisLine={false}
                dataKey="name"
                tick={{ fill: "#9AA1B0", fontSize: 12 }}
              />
              <YAxis hide />
              <Tooltip
                content={<CashPositionTooltip />}
                cursor={{ stroke: "#1f3c88", strokeWidth: 1 }}
              />

              <Area
                type="monotone"
                dataKey="value"
                stroke="#1f3c88"
                strokeWidth={3}
                fill="url(#colorGradient)"
                activeDot={{
                  r: 5,
                  stroke: "#1f3c88",
                  strokeWidth: 2,
                  fill: "#fff",
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default memo(CashPosition);
